// src/AppointmentsWithGroomerChange.js
import React, { useState } from "react";
import { apiFetch } from "./utils/apiUtils";
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
} from "@mui/material";
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

const AppointmentsWithGroomerChange = () => {
  // Get today's date for start date default value
  const getToday = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  };

  // Get date 7 days from today for end date default value
  const getWeekFromToday = () => {
    const now = new Date();
    // Add 7 days to current date
    now.setDate(now.getDate() + 7);
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  };

  const [startDate, setStartDate] = useState(getToday());
  const [endDate, setEndDate] = useState(getWeekFromToday());
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reportGeneratedAt, setReportGeneratedAt] = useState(null);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("desc");

  const handleRunReport = () => {
    if (!startDate || !endDate) {
      setError("Please select both a start and end date.");
      return;
    }
    
    setLoading(true);
    setError("");
    
    // Call the API endpoint
    apiFetch(`/api/reports/appointments-with-groomer-change?start_date=${startDate}&end_date=${endDate}`)
      .then((res) => {
        if (!res.ok) {
          return res.text().then(text => { 
            throw new Error("Failed to fetch report data: " + text);
          });
        }
        return res.json();
      })
      .then((data) => {
        setReportData(data);
        setReportGeneratedAt(new Date());
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error running report:", err);
        setError("Error running report: " + err.message);
        setLoading(false);
      });
  };

  // Handle column sorting
  const handleSort = (column) => {
    // If clicking the same column, toggle direction
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // If clicking a new column, set it as the sort column with desc direction
      setSortColumn(column);
      setSortDirection("desc");
    }
  };

  // Get sorted data based on current sort column and direction
  const getSortedData = () => {
    if (!reportData || !sortColumn) return reportData;
    
    // Create a copy of the data for sorting
    const dataToSort = [...reportData];
    
    // Sort the data
    dataToSort.sort((a, b) => {
      let valueA = a[sortColumn];
      let valueB = b[sortColumn];
      
      // Handle string comparison
      if (typeof valueA === 'string') {
        valueA = valueA?.toLowerCase() || '';
        valueB = valueB?.toLowerCase() || '';
        return sortDirection === "asc" 
          ? valueA.localeCompare(valueB) 
          : valueB.localeCompare(valueA);
      } 
      // Handle numeric values
      else {
        // Convert to numbers for comparison
        valueA = Number(valueA) || 0;
        valueB = Number(valueB) || 0;
        return sortDirection === "asc" ? valueA - valueB : valueB - valueA;
      }
    });
    
    return dataToSort;
  };

  // Format date values
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    
    // Parse the date parts explicitly to avoid timezone issues
    // This ensures the date is interpreted in local time without shifting
    const [year, month, day] = dateStr.split('-').map(num => parseInt(num, 10));
    const localDate = new Date(year, month - 1, day); // month is 0-indexed in JS Date
    
    const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
    return localDate.toLocaleDateString('en-US', options);
  };
  
  // Get sorted data
  const sortedData = reportData ? getSortedData() : null;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Appointments with Groomer Change
      </Typography>
      
      {/* Report criteria form */}
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 400, mb: 2 }}
      >
        <TextField
          label="Start Date"
          type="date"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
        <TextField
          label="End Date"
          type="date"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />
        
        <Button 
          variant="contained" 
          onClick={handleRunReport}
          disabled={loading}
        >
          {loading ? "Loading..." : "Run Report"}
        </Button>
      </Box>
      
      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {/* Report header with title and generation timestamp */}
      {reportGeneratedAt && (
        <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6">Appointments with Groomer Change</Typography>
          <Typography variant="caption">
            Report period: {startDate} to {endDate} | Generated: {reportGeneratedAt.toLocaleString()}
            <br />
            Click on column headers to dynamically sort the columns.
          </Typography>
        </Box>
      )}
      
      {/* Report results display */}
      {reportData && reportData.length > 0 ? (
        <Box sx={{ mt: 2 }}>
          <TableContainer component={Paper} sx={{ mb: 4, overflowX: 'auto', maxWidth: '100%' }}>
            <Table size="small" sx={{ 
              '& .MuiTableCell-root': { 
                fontSize: '0.75rem', 
                padding: '6px 16px',
                whiteSpace: 'nowrap'
              }
            }}>
              <TableHead>
                <TableRow sx={{ fontWeight: 'bold', '& th': { fontWeight: 'bold' } }}>
                  <TableCell 
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      fontWeight: 'bold'
                    }}
                    onClick={() => handleSort("Assigned_Groomer")}
                  >
                    Assigned Groomer
                    {sortColumn === "Assigned_Groomer" && (
                      sortDirection === "asc" ? 
                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      fontWeight: 'bold'
                    }}
                    onClick={() => handleSort("Last_Groomer")}
                  >
                    Last Groomer
                    {sortColumn === "Last_Groomer" && (
                      sortDirection === "asc" ? 
                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      fontWeight: 'bold'
                    }}
                    onClick={() => handleSort("Last_4_Groomers")}
                  >
                    Last 4 Groomers
                    {sortColumn === "Last_4_Groomers" && (
                      sortDirection === "asc" ? 
                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      fontWeight: 'bold'
                    }}
                    onClick={() => handleSort("Customer_FirstName")}
                  >
                    Customer First Name
                    {sortColumn === "Customer_FirstName" && (
                      sortDirection === "asc" ? 
                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      fontWeight: 'bold'
                    }}
                    onClick={() => handleSort("Customer_LastName")}
                  >
                    Customer Last Name
                    {sortColumn === "Customer_LastName" && (
                      sortDirection === "asc" ? 
                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      fontWeight: 'bold'
                    }}
                    onClick={() => handleSort("Pet_Name")}
                  >
                    Pet Name
                    {sortColumn === "Pet_Name" && (
                      sortDirection === "asc" ? 
                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      fontWeight: 'bold'
                    }}
                    onClick={() => handleSort("Appt_Date")}
                  >
                    Appointment Date
                    {sortColumn === "Appt_Date" && (
                      sortDirection === "asc" ? 
                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      fontWeight: 'bold'
                    }}
                    onClick={() => handleSort("Appt_Time")}
                  >
                    Appointment Time
                    {sortColumn === "Appt_Time" && (
                      sortDirection === "asc" ? 
                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      fontWeight: 'bold'
                    }}
                    onClick={() => handleSort("city")}
                  >
                    City
                    {sortColumn === "city" && (
                      sortDirection === "asc" ? 
                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      fontWeight: 'bold'
                    }}
                    onClick={() => handleSort("state")}
                  >
                    State
                    {sortColumn === "state" && (
                      sortDirection === "asc" ? 
                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      fontWeight: 'bold'
                    }}
                    onClick={() => handleSort("cust_phone")}
                  >
                    Phone Number
                    {sortColumn === "cust_phone" && (
                      sortDirection === "asc" ? 
                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      fontWeight: 'bold'
                    }}
                    onClick={() => handleSort("cust_email")}
                  >
                    Email
                    {sortColumn === "cust_email" && (
                      sortDirection === "asc" ? 
                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      fontWeight: 'bold'
                    }}
                    onClick={() => handleSort("Created_By")}
                  >
                    Created By
                    {sortColumn === "Created_By" && (
                      sortDirection === "asc" ? 
                      <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} /> : 
                      <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedData && sortedData.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{row.Assigned_Groomer}</TableCell>
                    <TableCell>{row.Last_Groomer}</TableCell>
                    <TableCell>{row.Last_4_Groomers}</TableCell>
                    <TableCell>{row.Customer_FirstName}</TableCell>
                    <TableCell>{row.Customer_LastName}</TableCell>
                    <TableCell>{row.Pet_Name}</TableCell>
                    <TableCell>{formatDate(row.Appt_Date)}</TableCell>
                    <TableCell>{row.Appt_Time}</TableCell>
                    <TableCell>{row.city}</TableCell>
                    <TableCell>{row.state}</TableCell>
                    <TableCell>{row.cust_phone}</TableCell>
                    <TableCell>{row.cust_email}</TableCell>
                    <TableCell>{row.Created_By}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Button variant="contained" sx={{ mt: 2 }} onClick={() => window.print()}>
            Print / Save as PDF
          </Button>
        </Box>
      ) : reportGeneratedAt && (
        <Typography variant="body1" sx={{ mt: 4, textAlign: 'center' }}>
          No appointments with groomer changes found for the selected date range.
        </Typography>
      )}
    </Box>
  );
};

export default AppointmentsWithGroomerChange;